
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { isAccountLocked, recordFailedAttempt, resetFailedAttempts } from '@/contexts/auth/authUtils';
import { sanitizeEmail, validateEmail, isRateLimited, logSecurityEvent } from '@/utils/security';
import { AuthError } from '@supabase/supabase-js';
import { securityConfig } from '@/config/security';

export const useSignIn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Input validation and sanitization
      const sanitizedEmail = sanitizeEmail(email);
      
      if (!validateEmail(sanitizedEmail)) {
        throw new Error('Please enter a valid email address');
      }
      
      // Rate limiting check (configurable)
      if (isRateLimited(`signin_${sanitizedEmail}`, securityConfig.signInRateLimit.maxAttempts, securityConfig.signInRateLimit.windowMs)) {
        logSecurityEvent('rate_limit_exceeded', { email: sanitizedEmail, action: 'signin' });
        throw new Error('Too many login attempts. Please try again later.');
      }
      
      // Check if account is locked using centralized auth utils
      const { locked, remainingTime } = isAccountLocked(sanitizedEmail);
      if (locked) {
        const minutes = Math.ceil((remainingTime || 0) / (60 * 1000));
        logSecurityEvent('account_locked_attempt', { email: sanitizedEmail });
        throw new Error(`This account is temporarily locked due to too many failed attempts. Please try again in ${minutes} minutes.`);
      }
      
      console.log("Attempting login with email:", sanitizedEmail);
      
      // Clear any existing sessions first to prevent state conflicts
      await supabase.auth.signOut({ scope: 'local' });
      
      // Attempt sign in with sanitized input
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: sanitizedEmail, 
        password 
      });
      
      if (error) {
        recordFailedAttempt(sanitizedEmail);
        logSecurityEvent('signin_failed', { 
          email: sanitizedEmail, 
          error: error.message,
          ip: 'client-side' // Would be populated server-side in production
        });
        console.error("Login failed:", error.message);
        throw error;
      }
      
      // Reset failed attempts on successful login
      resetFailedAttempts(sanitizedEmail);
      
      logSecurityEvent('signin_success', { 
        email: sanitizedEmail,
        userId: data?.session?.user?.id 
      });
      
      console.log("Login successful, session established:", data?.session?.user?.id);
      
      toast({
        title: "Welcome back!",
        description: "You've successfully signed in.",
      });
      
      return { data, error: null };
    } catch (error) {
      console.error('Error signing in:', error);
      
      const errorMessage = error instanceof AuthError || error instanceof Error
        ? error.message
        : 'An unknown error occurred during sign in';
      
      setError(error instanceof Error ? error : new Error(errorMessage));
      
      toast({
        title: "Login failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      return { data: null, error: { message: errorMessage } };
    } finally {
      setIsLoading(false);
    }
  };

  return { signIn, isLoading, error };
};
